// Tiny CSS subset for SVG <style> blocks: simple selectors (tag, .class, #id, tag.class) only.
// Design tools (Illustrator, Figma, Inkscape) emit exactly this, usually ".cls-1{fill:#fff}".

export type Declarations = Record<string, string>;

export interface CssRule {
  readonly selector: SimpleSelector;
  readonly declarations: Declarations;
  readonly specificity: number;
  readonly order: number;
}

export interface SimpleSelector {
  readonly tag?: string;
  readonly id?: string;
  readonly classes: readonly string[];
}

const SELECTOR_RE = /^([a-zA-Z][\w-]*)?((?:[.#][\w-]+)*)$/;

const parseSelector = (raw: string): SimpleSelector | null => {
  const match = SELECTOR_RE.exec(raw.trim());
  if (!match || (!match[1] && !match[2])) return null;
  const parts = match[2].match(/[.#][\w-]+/g) ?? [];
  const ids = parts.filter((p) => p[0] === "#").map((p) => p.slice(1));
  if (ids.length > 1) return null;
  return {
    tag: match[1]?.toLowerCase(),
    id: ids[0],
    classes: parts.filter((p) => p[0] === ".").map((p) => p.slice(1)),
  };
};

const specificityOf = (s: SimpleSelector): number => (s.id ? 100 : 0) + s.classes.length * 10 + (s.tag ? 1 : 0);

/** Parses "a: b; c: d" into a lowercase-keyed map. `!important` is stripped, not honoured. */
export const parseDeclarations = (block: string): Declarations => {
  const result: Declarations = {};
  block.split(";").forEach((decl) => {
    const colon = decl.indexOf(":");
    if (colon === -1) return;
    const key = decl.slice(0, colon).trim().toLowerCase();
    const value = decl
      .slice(colon + 1)
      .replace(/!important/i, "")
      .trim();
    if (key && value) result[key] = value;
  });
  return result;
};

// Drops @-rules (and their nested blocks) so @import / @font-face never reach the parser.
const stripAtRules = (css: string): string => {
  let out = "";
  let pos = 0;
  while (pos < css.length) {
    const at = css.indexOf("@", pos);
    if (at === -1) {
      out += css.slice(pos);
      break;
    }
    out += css.slice(pos, at);
    const semicolon = css.indexOf(";", at);
    const brace = css.indexOf("{", at);
    if (brace === -1 || (semicolon !== -1 && semicolon < brace)) {
      pos = semicolon === -1 ? css.length : semicolon + 1;
      continue;
    }
    let depth = 0;
    let i = brace;
    for (; i < css.length; i += 1) {
      if (css[i] === "{") depth += 1;
      else if (css[i] === "}" && --depth === 0) break;
    }
    pos = i + 1;
  }
  return out;
};

export const parseStylesheet = (css: string, startOrder = 0): CssRule[] => {
  const rules: CssRule[] = [];
  const cleaned = stripAtRules(css.replace(/\/\*[\s\S]*?\*\//g, ""));
  const blockRe = /([^{}]+)\{([^{}]*)\}/g;
  let order = startOrder;
  for (let match = blockRe.exec(cleaned); match; match = blockRe.exec(cleaned)) {
    const declarations = parseDeclarations(match[2]);
    match[1].split(",").forEach((raw) => {
      const selector = parseSelector(raw);
      if (selector) rules.push({ selector, declarations, specificity: specificityOf(selector), order: order++ });
    });
  }
  return rules;
};

export const matches = (selector: SimpleSelector, tag: string, attrs: Readonly<Record<string, string>>): boolean => {
  if (selector.tag && selector.tag !== tag.toLowerCase()) return false;
  if (selector.id && selector.id !== attrs.id) return false;
  if (selector.classes.length === 0) return true;
  const classes = (attrs.class ?? "").split(/\s+/);
  return selector.classes.every((c) => classes.includes(c));
};

/** Cascaded declarations for one element, lowest priority first so later spreads win. */
export const cascade = (
  rules: readonly CssRule[],
  tag: string,
  attrs: Readonly<Record<string, string>>
): Declarations =>
  rules
    .filter((rule) => matches(rule.selector, tag, attrs))
    .sort((x, y) => x.specificity - y.specificity || x.order - y.order)
    .reduce<Declarations>((acc, rule) => ({ ...acc, ...rule.declarations }), {});
